import React from 'react';
import { motion } from 'framer-motion';
import { CalendarIcon, CheckCircle2Icon, AlertCircleIcon, ArrowRightIcon } from 'lucide-react';
import { OPENFINANCE_THEME } from '../../constants/openFinanceTheme';
export interface BillPaymentCardProps {
  merchantName: string;
  merchantLogo?: string;
  amount: number;
  currency: string;
  dueDate: string;
  daysUntilDue: number;
  hasVRP: boolean;
  onPayNow?: () => void;
  onSetupAutoPay?: () => void;
}
export function BillPaymentCard({
  merchantName,
  merchantLogo,
  amount,
  currency,
  dueDate,
  daysUntilDue,
  hasVRP,
  onPayNow,
  onSetupAutoPay
}: BillPaymentCardProps) {
  const isOverdue = daysUntilDue < 0;
  const isDueToday = daysUntilDue === 0;
  const isDueSoon = daysUntilDue > 0 && daysUntilDue <= 3;
  const getStatusColor = () => {
    if (isOverdue) return '#C62828'; // Red
    if (isDueToday) return '#E65100'; // Orange
    if (isDueSoon) return '#F57C00'; // Amber
    return OPENFINANCE_THEME.colors.success; // Green
  };
  const getStatusText = () => {
    if (isOverdue) return 'Overdue';
    if (isDueToday) return 'Due today';
    if (daysUntilDue === 1) return 'Due tomorrow';
    return `Due in ${daysUntilDue} days`;
  };
  const statusColor = getStatusColor();
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="bg-white rounded-2xl p-4 border-2 shadow-sm" style={{
    borderColor: OPENFINANCE_THEME.colors.border
  }} data-id="element-1538">
      <div className="flex items-start justify-between mb-3" data-id="element-1539">
        {/* Left: Merchant info */}
        <div className="flex items-center gap-3" data-id="element-1540">
          {merchantLogo && <div className="w-12 h-12 rounded-xl flex items-center justify-center p-2" style={{
          backgroundColor: OPENFINANCE_THEME.colors.borderLight
        }} data-id="element-1541">
              <img src={merchantLogo} alt={merchantName} className="w-full h-full object-contain" data-id="element-1542" />
            </div>}
          <div data-id="element-1543">
            <div className="font-bold text-base" style={{
            color: OPENFINANCE_THEME.colors.text,
            fontFamily: OPENFINANCE_THEME.fonts.heading
          }} data-id="element-1544">
              {merchantName}
            </div>
            <div className="flex items-center gap-2 mt-1" data-id="element-1545">
              <CalendarIcon className="w-3.5 h-3.5" style={{
              color: statusColor
            }} data-id="element-1546" />
              <span className="text-xs font-semibold" style={{
              color: statusColor
            }} data-id="element-1547">
                {getStatusText()}
              </span>
            </div>
          </div>
        </div>

        {/* Right: Amount */}
        <div className="text-right" data-id="element-1548">
          <div className="text-2xl font-bold" style={{
          color: OPENFINANCE_THEME.colors.text,
          fontFamily: OPENFINANCE_THEME.fonts.heading
        }} data-id="element-1549">
            {currency} {amount.toLocaleString()}
          </div>
          <div className="text-xs mt-1" style={{
          color: OPENFINANCE_THEME.colors.textMuted
        }} data-id="element-1550">
            {dueDate}
          </div>
        </div>
      </div>

      {/* VRP Status or Actions */}
      {hasVRP ? <div className="flex items-center gap-2 p-3 rounded-xl" style={{
      backgroundColor: OPENFINANCE_THEME.colors.success + '10',
      border: `1px solid ${OPENFINANCE_THEME.colors.success}30`
    }} data-id="element-1551">
          <CheckCircle2Icon className="w-5 h-5 flex-shrink-0" style={{
        color: OPENFINANCE_THEME.colors.success
      }} data-id="element-1552" />
          <div className="flex-1" data-id="element-1553">
            <div className="text-xs font-semibold" style={{
          color: OPENFINANCE_THEME.colors.success
        }} data-id="element-1554">
              Auto-pay enabled
            </div>
            <div className="text-xs" style={{
          color: OPENFINANCE_THEME.colors.textMuted
        }} data-id="element-1555">
              Payment will be processed automatically
            </div>
          </div>
        </div> : <div className="flex gap-2" data-id="element-1556">
          {onPayNow && <button onClick={onPayNow} className="flex-1 px-4 py-2.5 rounded-xl font-semibold text-sm text-white transition-all hover:shadow-lg active:scale-[0.98] flex items-center justify-center gap-2" style={{
        background: `linear-gradient(135deg, ${OPENFINANCE_THEME.colors.accent500} 0%, ${OPENFINANCE_THEME.colors.accent600} 100%)`
      }} data-id="element-1557">
              <span data-id="element-1558">Pay Now</span>
              <ArrowRightIcon className="w-4 h-4" data-id="element-1559" />
            </button>}
          {onSetupAutoPay && <button onClick={onSetupAutoPay} className="flex-1 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all border-2 hover:bg-gray-50 active:scale-[0.98]" style={{
        borderColor: OPENFINANCE_THEME.colors.border,
        color: OPENFINANCE_THEME.colors.text
      }} data-id="element-1560">
              Set Up Auto-Pay
            </button>}
        </div>}

      {/* Warning for overdue */}
      {isOverdue && <motion.div initial={{
      opacity: 0,
      height: 0
    }} animate={{
      opacity: 1,
      height: 'auto'
    }} className="mt-3 flex items-start gap-2 p-3 rounded-xl" style={{
      backgroundColor: '#FFEBEE',
      border: '1px solid #F4433630'
    }} data-id="element-1561">
          <AlertCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5 text-red-600" data-id="element-1562" />
          <div className="text-xs text-red-600" data-id="element-1563">
            <span className="font-semibold" data-id="element-1564">Payment overdue.</span> Please pay
            as soon as possible to avoid late fees.
          </div>
        </motion.div>}
    </motion.div>;
}